/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Letter Templates API
 * API for managing letter templates and placeholders within an organization. All operations are scoped to the authenticated user\'s organization.  ## Letter Templates  Letter templates define reusable document templates with placeholders that can be filled in when generating letters for clients.  ### Status lifecycle - DRAFT: Initial status, template is being edited - ACTIVE: Template is published and available for use - ARCHIVED: Template is no longer available for new letters  ### Versioning & lineage When an ACTIVE template is updated, a new version is created and the original becomes ARCHIVED. This preserves the exact template used for existing letters.  Every version shares a stable `lineageId` (the chain of edits descended from one original template); the newest version of each lineage is its **head**. The list endpoint returns one row per lineage (heads only) with a computed `versionCount`, while `GET /{id}/versions` returns the full ordered history of a lineage. See ADR-032 for the data model (`lineage_id` / `is_latest`).  ## Authentication All endpoints require Firebase Authentication JWT token in `Authorization: Bearer <token>` header.  ## Error Responses All error responses follow RFC 7807 Problem Details format. 
 *
 * The version of the OpenAPI document: 1.0.0
 * Contact: support@scrivio.nl
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

export interface CreateLetterTemplateRequest {
    /**
     * Template name
     */
    'name': string;
    /**
     * Optional description
     */
    'description'?: string | null;
    /**
     * Template content with placeholders
     */
    'content': string;
    /**
     * JSON definition of available placeholders
     */
    'placeholders'?: string | null;
    /**
     * Print letters from this template on the organisation\'s letterhead when one is active. Absent means on. 
     */
    'useLetterhead'?: boolean | null;
}
/**
 * The filling of one form a letter\'s answer placeholders quote
 */
export interface FormSubmissionSelection {
    'formId': string;
    'formSubmissionId': string;
}
export interface GenerateLetterRequest {
    /**
     * ID of the letter template to use (must be ACTIVE)
     */
    'templateId': string;
    /**
     * ID of the client the letter is for
     */
    'clientId': string;
    /**
     * If true, save the generated PDF to the client\'s dossier and create a GeneratedLetter audit record. If false, return the PDF directly without persisting. 
     */
    'saveToDossier'?: boolean;
    /**
     * Optional HTML content to use instead of the template content. When provided, this content is used as-is (after sanitization) for PDF generation, allowing users to edit the letter before generating. The templateId is still required for audit purposes. 
     */
    'htmlContent'?: string | null;
    /**
     * Which filling of a form the letter\'s answer placeholders quote — see `LetterPreviewRequest.formSubmissionSelections`. Used when the letter is rendered from the template, and always for the placeholder snapshot recorded with a saved letter, so the snapshot holds the answers the behandelaar chose. 
     */
    'formSubmissionSelections'?: Array<FormSubmissionSelection>;
}
/**
 * Response when letter is generated and saved to dossier
 */
export interface GenerateLetterResponse {
    /**
     * ID of the GeneratedLetter audit record
     */
    'letterId': string;
    /**
     * ID of the document saved in the dossier
     */
    'documentId': string;
    /**
     * URL to download the generated PDF
     */
    'downloadUrl': string;
    /**
     * Timestamp when the letter was generated (milliseconds since epoch)
     */
    'generatedAt': number;
}
/**
 * The stored content of a generated letter, as its PDF was rendered from it
 */
export interface GeneratedLetterContent {
    /**
     * Unique identifier of the generated letter
     */
    'id': string;
    /**
     * ID of the letter template used
     */
    'templateId': string;
    /**
     * Name of the letter template used
     */
    'templateName': string;
    /**
     * The HTML the letter\'s PDF was rendered from, including edits made before generating
     */
    'contentHtml': string;
}
/**
 * One page of the generated letters for a client, newest first
 */
export interface GeneratedLetterListResponse {
    /**
     * The letters on this page, sorted by generation date descending
     */
    'content': Array<GeneratedLetterSummary>;
    /**
     * The same letters as `content`. Kept only for a browser tab still running a frontend built before this list was paged, and removed once those have reloaded; read `content`. 
     * @deprecated
     */
    'letters': Array<GeneratedLetterSummary>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of letters per page
     */
    'size': number;
    /**
     * Total number of letters
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
/**
 * Summary information about a generated letter
 */
export interface GeneratedLetterSummary {
    /**
     * Unique identifier of the generated letter
     */
    'id': string;
    /**
     * ID of the letter template used
     */
    'templateId': string;
    /**
     * Name of the letter template used
     */
    'templateName': string;
    /**
     * The HTML the letter was rendered from is stored, so it can be reopened in the editor and generated again. False for letters generated before that content was kept. 
     */
    'editable': boolean;
    /**
     * Timestamp when the letter was generated (milliseconds since epoch)
     */
    'generatedAt': number;
    /**
     * Display name of the user who generated the letter
     */
    'generatedByName': string;
    /**
     * ID of the document in the client\'s dossier
     */
    'documentId': string;
    /**
     * Every ZorgMail attempt for this letter, newest first — accepted *and* refused. 
     */
    'zorgMailSends'?: Array<ZorgMailSendRecord>;
}
/**
 * A form whose answers the letter quotes, with the fillings the behandelaar can choose between. Only a form with more than one submission is a choice. 
 */
export interface LetterAnswerForm {
    'formId': string;
    'formTitle': string;
    /**
     * The submission the letter quotes — the selected one, or else the latest
     */
    'selectedFormSubmissionId': string;
    /**
     * The client\'s submissions of this form that answer a question the letter uses, latest first
     */
    'submissions': Array<LetterAnswerFormSubmission>;
}
export interface LetterAnswerFormSubmission {
    'formSubmissionId': string;
    /**
     * When the submission was started (milliseconds since epoch)
     */
    'submittedAt': number;
    /**
     * Start of the appointment the submission belongs to, if any (milliseconds since epoch)
     */
    'appointmentStartTime'?: number | null;
}
export interface LetterPageResponse {
    /**
     * The letter stylesheet with the page margins, as the renderer inlines it into every letter
     */
    'stylesheet': string;
    /**
     * Whether the letter is printed on the organisation\'s active letterhead
     */
    'letterhead': boolean;
    /**
     * Whether pages 2 and on are printed on a letterhead page of their own; without one they are plain
     */
    'letterheadHasFollowingPage': boolean;
}
export interface LetterPreviewRequest {
    /**
     * ID of the letter template to use (must be ACTIVE)
     */
    'templateId': string;
    /**
     * ID of the client the letter is for
     */
    'clientId': string;
    /**
     * Which filling of a form the letter\'s answer placeholders quote, for forms the client has filled in more than once (#4637). Every answer placeholder whose question the chosen submission answers follows it; a form left out defaults to each question\'s most recent non-blank answer.  When a question sits in two selected forms, the more recent of the two selected submissions answers it. Each submission must be live, belong to the client and be a filling of the named form, and a form may be named once — otherwise 400. 
     */
    'formSubmissionSelections'?: Array<FormSubmissionSelection>;
}
/**
 * Letter preview with rendered HTML and missing placeholder information
 */
export interface LetterPreviewResponse {
    /**
     * Rendered HTML content of the letter with placeholders resolved
     */
    'html': string;
    /**
     * List of placeholders that could not be resolved or were empty
     */
    'missingPlaceholders': Array<MissingPlaceholder>;
    /**
     * The forms whose answers the letter quotes, each with the submission it quotes and the ones it could quote instead (#4637) 
     */
    'answerForms': Array<LetterAnswerForm>;
}
/**
 * Template content rendered as a standalone HTML document
 */
export interface LetterTemplateContentPreviewResponse {
    /**
     * Complete HTML document including the letter\'s print stylesheet. Intended for a sandboxed iframe, so the letter\'s own styling applies and app styles cannot leak in. 
     */
    'html': string;
}
export interface LetterTemplateListResponse {
    'content': Array<LetterTemplateResponse>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of items per page
     */
    'size': number;
    /**
     * Total number of items
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
export interface LetterTemplateResponse {
    /**
     * Letter template unique identifier
     */
    'id': string;
    /**
     * Organization this template belongs to
     */
    'organizationId': string;
    /**
     * Template name
     */
    'name': string;
    /**
     * Optional description of the template
     */
    'description'?: string | null;
    /**
     * Template content with placeholders
     */
    'content': string;
    /**
     * JSON definition of available placeholders
     */
    'placeholders'?: string | null;
    'status': LetterTemplateStatus;
    /**
     * Template version number
     */
    'version': number;
    /**
     * ID of the previous version (for version history)
     */
    'previousVersionId'?: string | null;
    /**
     * Stable identity shared by every version in this template\'s lineage (chain of edits). Equal to the id of the lineage\'s first version. 
     */
    'lineageId': string;
    /**
     * Total number of versions in this template\'s lineage. For example, a value of 3 means the UI can show \"2 oudere versies\". 
     */
    'versionCount': number;
    /**
     * Whether letters from this template are printed on the organisation\'s letterhead when one is active. Off for a letter that must stay plain, such as one sent as a form. 
     */
    'useLetterhead': boolean;
    /**
     * ID of the user who created this template
     */
    'createdById': string;
    /**
     * Timestamp when created (milliseconds since epoch)
     */
    'createdAt': number;
    /**
     * Timestamp when last updated (milliseconds since epoch)
     */
    'updatedAt': number;
}


/**
 * Letter template status: - DRAFT: Template is being edited, not yet available for use - ACTIVE: Template is published and available for generating letters - ARCHIVED: Template is no longer available for new letters 
 */

export const LetterTemplateStatus = {
    Draft: 'DRAFT',
    Active: 'ACTIVE',
    Archived: 'ARCHIVED'
} as const;

export type LetterTemplateStatus = typeof LetterTemplateStatus[keyof typeof LetterTemplateStatus];


export interface LetterTemplateVersionListResponse {
    /**
     * Versions of the lineage, newest first
     */
    'content': Array<LetterTemplateVersionSummary>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of items per page
     */
    'size': number;
    /**
     * Total number of versions in the lineage
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
/**
 * Lightweight summary of a single version within a lineage. Deliberately omits `content` — use `getLetterTemplateById` to fetch a version\'s full body. 
 */
export interface LetterTemplateVersionSummary {
    /**
     * Unique identifier of this version
     */
    'id': string;
    /**
     * Version number within the lineage
     */
    'version': number;
    'status': LetterTemplateStatus;
    /**
     * ID of the user who created this version
     */
    'createdById': string;
    /**
     * Timestamp when this version was created (milliseconds since epoch)
     */
    'createdAt': number;
}


/**
 * Which letterhead PDF: FIRST lies under page 1 of a letter, FOLLOWING under pages 2 and on. 
 */

export const LetterheadPage = {
    First: 'FIRST',
    Following: 'FOLLOWING'
} as const;

export type LetterheadPage = typeof LetterheadPage[keyof typeof LetterheadPage];


export interface LetterheadResponse {
    'id': string;
    /**
     * Whether pages 2 and on have a letterhead of their own; without one they are plain
     */
    'hasFollowingPage': boolean;
    'marginTopMm': number;
    'marginBottomMm': number;
    'marginLeftMm': number;
    'marginRightMm': number;
    /**
     * Whether new letters are printed on it
     */
    'active': boolean;
    /**
     * Timestamp of the last change (milliseconds since epoch)
     */
    'updatedAt': number;
}
/**
 * Information about a placeholder that could not be resolved
 */
export interface MissingPlaceholder {
    /**
     * The placeholder key (e.g., \"client.bsn\")
     */
    'key': string;
    /**
     * Dutch description of the placeholder
     */
    'description': string;
    /**
     * True if this is an answer placeholder referencing an archived question. Archived questions can still be resolved but may indicate outdated template content. 
     */
    'isArchivedQuestion'?: boolean;
}
/**
 * Categories of placeholders: - CLIENT: Client data (name, address, contact info) - ANSWER: Form question answers - DATE: Date formatting placeholders - BEHANDELAAR: Current user/therapist data - ORGANIZATION: Organization data - ZORGTRAJECT: Care trajectory data (name, dates, setting, diagnosis, hoofdbehandelaar) - VERWIJZER: Referrer data from the client\'s active care trajectory - ZORGVRAAG: Care needs and offer (aanmeldklacht, hulpvraag, doelen, hulpaanbod) - HUISARTS: The client\'s own huisarts, from their care circle — never the zorgtraject verwijzer 
 */

export const PlaceholderCategory = {
    Client: 'CLIENT',
    Answer: 'ANSWER',
    Date: 'DATE',
    Behandelaar: 'BEHANDELAAR',
    Organization: 'ORGANIZATION',
    Zorgtraject: 'ZORGTRAJECT',
    Verwijzer: 'VERWIJZER',
    Zorgvraag: 'ZORGVRAAG',
    Huisarts: 'HUISARTS'
} as const;

export type PlaceholderCategory = typeof PlaceholderCategory[keyof typeof PlaceholderCategory];


/**
 * A group of placeholders within a category
 */
export interface PlaceholderCategoryGroup {
    /**
     * Dutch display name for the category
     */
    'name': string;
    'key': PlaceholderCategory;
    /**
     * List of placeholders in this category
     */
    'placeholders': Array<PlaceholderDefinition>;
}


/**
 * A single placeholder definition
 */
export interface PlaceholderDefinition {
    /**
     * The placeholder syntax to use in templates. Format: {{category.field}} for static fields or {{answers[\'questionId\']}} for dynamic answers. 
     */
    'key': string;
    /**
     * Dutch description of the placeholder for the UI
     */
    'description': string;
    /**
     * Short Dutch \"<who> · <what>\" name the template editor shows on the placeholder\'s chip and searches on, in place of the key. The key stays what is inserted and stored. 
     */
    'label': string;
    /**
     * Example resolved value to show in the UI
     */
    'example': string;
    /**
     * Question UUID (only for ANSWER category placeholders)
     */
    'questionId'?: string;
    /**
     * The question\'s organization-unique key — the \"sleutel\" the author invented when creating the question (only for ANSWER category placeholders). It is a search and display affordance: the key that is inserted stays answers[\'questionId\'], which is the spelling the renderer resolves. 
     */
    'questionKey'?: string;
    /**
     * True for an ANSWER placeholder whose question is archived. Its answers still resolve, so a template that already names it keeps working and the editor can label its badge — but it is not offered for insertion into a template. 
     */
    'archived'?: boolean;
}
/**
 * Response containing all available placeholders grouped by category
 */
export interface PlaceholderListResponse {
    /**
     * List of placeholder categories with their placeholders
     */
    'categories': Array<PlaceholderCategoryGroup>;
}
export interface PreviewLetterTemplateContentRequest {
    /**
     * Template content as it currently stands in the editor, unsaved
     */
    'content': string;
    /**
     * The template\'s letterhead setting as it stands in the editor: when on and the organisation has an active letterhead, the body is inset by its margins. Absent means on. 
     */
    'useLetterhead'?: boolean | null;
}
/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}
/**
 * Request to send a generated letter to a recipient via Zivver secure email
 */
export interface SendLetterViaZivverRequest {
    /**
     * Recipient e-mail address the secure letter is sent to
     */
    'recipient': string;
    /**
     * Recipient verification method Zivver enforces for this message (ADR-028): - `SMS`: one-time code by text message (requires `verificationDetails.smsNumber`) - `EMAIL`: e-mail verification - `ACCESS_CODE`: pre-agreed access code (requires `verificationDetails.accessCode`) - `ZIVVER_ACCOUNT`: recipient logs in with a Zivver account 
     */
    'verificationMethod': SendLetterViaZivverRequestVerificationMethodEnum;
    'verificationDetails'?: SendLetterViaZivverRequestVerificationDetails | null;
    /**
     * Optional message shown to the recipient above the letter. When omitted a localized default greeting is used. 
     */
    'messageBody'?: string | null;
}

export const SendLetterViaZivverRequestVerificationMethodEnum = {
    Sms: 'SMS',
    Email: 'EMAIL',
    AccessCode: 'ACCESS_CODE',
    ZivverAccount: 'ZIVVER_ACCOUNT'
} as const;

export type SendLetterViaZivverRequestVerificationMethodEnum = typeof SendLetterViaZivverRequestVerificationMethodEnum[keyof typeof SendLetterViaZivverRequestVerificationMethodEnum];

/**
 * Extra detail required by some verification methods
 */
export interface SendLetterViaZivverRequestVerificationDetails {
    /**
     * Recipient mobile number, preferably in E.164 format, required when verificationMethod is SMS. Digits with an optional leading `+`, and spaces, hyphens or parentheses as separators — nothing else. The value is interpolated into the `zivver-access-right` MIME header, so anything that could terminate a header line is rejected here (#3440). 
     */
    'smsNumber'?: string | null;
    /**
     * Pre-agreed access code, required when verificationMethod is ACCESS_CODE. Printable ASCII without spaces: the code is the last space-separated field of the `zivver-access-right` MIME header, so a space would split it and a control character would terminate the header line (#3440). 
     */
    'accessCode'?: string | null;
}
/**
 * Result of a successful Zivver submission
 */
export interface SendLetterViaZivverResponse {
    /**
     * RFC 5322 Message-ID assigned by Zivver, when available
     */
    'messageId'?: string | null;
    /**
     * Timestamp the letter was submitted (milliseconds since epoch)
     */
    'sentAt': number;
}
/**
 * Who the letter goes to. Every field describes the **recipient** — the letter itself is already generated, and a MEDVRI is a second rendering of it rather than a new document (ADR-041 §5), so there is nothing to compose here. 
 */
export interface SendLetterViaZorgMailRequest {
    'recipient': ZorgMailMessageRecipient;
}
/**
 * ZorgMail accepted the message. Named *accepted* rather than *sent* everywhere it travels, because that is all that is known at this point — see the operation description. 
 */
export interface SendLetterViaZorgMailResponse {
    /**
     * The recorded attempt. It is what the letter\'s `zorgMailSends` will show afterwards, and where a status message retrieved by #4122 will attach. 
     */
    'sendId': string;
    /**
     * When ZorgMail accepted it (milliseconds since epoch)
     */
    'sentAt': number;
    /**
     * The RFC 5322 `Message-ID` the submission carried, when the gateway assigned one.
     */
    'messageId'?: string | null;
}
/**
 * The gateway to send the test message to. The only input there is.
 */
export interface SendZorgMailGatewayTestRequest {
    'gateway': ZorgMailTestGateway;
}


/**
 * All fields are optional. Only provided fields will be updated.
 */
export interface UpdateLetterTemplateRequest {
    /**
     * Template name
     */
    'name'?: string | null;
    /**
     * Optional description
     */
    'description'?: string | null;
    /**
     * Template content with placeholders
     */
    'content'?: string | null;
    /**
     * JSON definition of available placeholders
     */
    'placeholders'?: string | null;
    /**
     * Print letters from this template on the organisation\'s letterhead when one is active. Absent means unchanged. 
     */
    'useLetterhead'?: boolean | null;
}
/**
 * All fields are optional. Only provided fields will be updated.
 */
export interface UpdateLetterheadRequest {
    'marginTopMm'?: number | null;
    'marginBottomMm'?: number | null;
    'marginLeftMm'?: number | null;
    'marginRightMm'?: number | null;
    'active'?: boolean | null;
}
/**
 * What the gateway answered to one test message, as recorded.
 */
export interface ZorgMailGatewayTestResponse {
    'testSendId': string;
    'gateway': ZorgMailTestGateway;
    /**
     * When the test was sent (milliseconds since epoch)
     */
    'sentAt': number;
    /**
     * The gateway took the message. **Not a statement that it is valid** — the validatieverslag says that, in the practice\'s own ZorgMail mailbox. 
     */
    'accepted': boolean;
    /**
     * The RFC 5322 `Message-ID` the submission carried. Null on a refusal.
     */
    'messageId'?: string | null;
    /**
     * The name of the ZorgMail refusal (`NOT_PERMITTED_FOR_SENDER`, `SENDER_UNKNOWN`, `UNREACHABLE`, …), or null when accepted. A free string for the reason given on `ZorgMailSendRecord.refusal`. 
     */
    'refusal'?: string | null;
    /**
     * The Dutch sentence for `refusal`. Null when accepted.
     */
    'refusalReason'?: string | null;
}


/**
 * The confirmed recipient, normally echoed from a `ZorgMailRecipientCandidate` and possibly corrected by the sender.  `address` and `mailbox` are both required and are not derived from one another: `address` is what the SMTP `RCPT TO` names, `mailbox` is what the MEDVRI\'s own `envelope.recipientidentifier` carries, and the latter has no domain because the EDIFACT character set has no `@`. The remaining fields fill the message\'s `ontvanger` block, which identifies the recipient *inside* the message and is read by a person rather than a router. 
 */
export interface ZorgMailMessageRecipient {
    /**
     * The EDI address to route to — an AGB alias or a ZorgMail mailbox address.
     */
    'address': string;
    /**
     * The recipient\'s ZorgMail mailboxnummer, digits only.
     */
    'mailbox': string;
    /**
     * The recipient\'s name, as it should appear in the message\'s `ontvanger` block.
     */
    'name': string;
    'department'?: string | null;
    'organization'?: string | null;
    'street'?: string | null;
    'postalCode'?: string | null;
    'city'?: string | null;
    'phone'?: string | null;
}
/**
 * One attempt to hand a letter to ZorgMail.
 */
export interface ZorgMailSendRecord {
    'id': string;
    /**
     * When the attempt was made (milliseconds since epoch)
     */
    'sentAt': number;
    /**
     * ZorgMail took the message. **Deliberately not called delivered**: acceptance is acknowledgement of the submission, and a content failure bounces asynchronously into the practice\'s own ZorgMail mailbox, which Scrivio does not read. 
     */
    'accepted': boolean;
    /**
     * The EDI address the message was routed to, as it was at the time of sending.
     */
    'recipientAddress': string;
    /**
     * The recipient as the sender confirmed them. Stored rather than resolved again on read: the adresboek is a live search, and a practice that has since moved would make this row name someone who was never sent anything. 
     */
    'recipientName': string;
    'recipientOrganization'?: string | null;
    /**
     * The RFC 5322 `Message-ID` the submission carried. Null on a refusal. 
     */
    'messageId'?: string | null;
    /**
     * The name of the ZorgMail error condition, when the attempt was refused (`RECIPIENT_UNKNOWN`, `NOT_PERMITTED_FOR_SENDER`, `SENDER_UNKNOWN`, `UNREACHABLE`, …). Null when accepted.  A free string rather than an `enum:` on purpose, matching the column beneath it: the vocabulary tracks Enovation\'s 25 error conditions and will grow, and a stored code a later build no longer knows must still read as \"this send was refused\" rather than failing the letter\'s whole history. Branch on it if you recognise it; render `refusalReason` if you do not. 
     */
    'refusal'?: string | null;
    /**
     * The Dutch sentence for `refusal`, resolved at read time from the refusal constant so correcting the wording corrects every historical row at once. Null when accepted, and also when `refusal` names a constant this build no longer has. 
     */
    'refusalReason'?: string | null;
}
/**
 * Which of Enovation\'s test gateways to send to: - VALIDATIE: `gateway.validatie@lms.lifeline.nl` — answers every message with a   validatieverslag in the practice\'s own ZorgMail mailbox - TEST: `gateway.test@lms.lifeline.nl` — delivers a valid message back to the sender 
 */

export const ZorgMailTestGateway = {
    Validatie: 'VALIDATIE',
    Test: 'TEST'
} as const;

export type ZorgMailTestGateway = typeof ZorgMailTestGateway[keyof typeof ZorgMailTestGateway];



/**
 * LetterTemplatePlaceholdersApi - axios parameter creator
 */
export const LetterTemplatePlaceholdersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns all available placeholders grouped by category. Each placeholder includes its key, description (Dutch), category, and an example value. For the ANSWER category, returns available questions from the organization\'s forms. 
         * @summary List available placeholders for template editor
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listPlaceholders: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/letter-templates/placeholders`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * LetterTemplatePlaceholdersApi - functional programming interface
 */
export const LetterTemplatePlaceholdersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = LetterTemplatePlaceholdersApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns all available placeholders grouped by category. Each placeholder includes its key, description (Dutch), category, and an example value. For the ANSWER category, returns available questions from the organization\'s forms. 
         * @summary List available placeholders for template editor
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listPlaceholders(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PlaceholderListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listPlaceholders(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LetterTemplatePlaceholdersApi.listPlaceholders']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * LetterTemplatePlaceholdersApi - factory interface
 */
export const LetterTemplatePlaceholdersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = LetterTemplatePlaceholdersApiFp(configuration)
    return {
        /**
         * Returns all available placeholders grouped by category. Each placeholder includes its key, description (Dutch), category, and an example value. For the ANSWER category, returns available questions from the organization\'s forms. 
         * @summary List available placeholders for template editor
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listPlaceholders(options?: RawAxiosRequestConfig): AxiosPromise<PlaceholderListResponse> {
            return localVarFp.listPlaceholders(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * LetterTemplatePlaceholdersApi - object-oriented interface
 */
export class LetterTemplatePlaceholdersApi extends BaseAPI {
    /**
     * Returns all available placeholders grouped by category. Each placeholder includes its key, description (Dutch), category, and an example value. For the ANSWER category, returns available questions from the organization\'s forms. 
     * @summary List available placeholders for template editor
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listPlaceholders(options?: RawAxiosRequestConfig) {
        return LetterTemplatePlaceholdersApiFp(this.configuration).listPlaceholders(options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * LetterTemplatesApi - axios parameter creator
 */
export const LetterTemplatesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Change template status from DRAFT to ACTIVE. Only DRAFT templates can be activated. 
         * @summary Activate letter template
         * @param {string} letterTemplateId Letter template UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activateLetterTemplate: async (letterTemplateId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'letterTemplateId' is not null or undefined
            assertParamExists('activateLetterTemplate', 'letterTemplateId', letterTemplateId)
            const localVarPath = `/api/v1/letter-templates/{letterTemplateId}/activate`
                .replace(`{${"letterTemplateId"}}`, encodeURIComponent(String(letterTemplateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Change template status to ARCHIVED. Both DRAFT and ACTIVE templates can be archived. Archived templates cannot be used for new letters. 
         * @summary Archive letter template
         * @param {string} letterTemplateId Letter template UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        archiveLetterTemplate: async (letterTemplateId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'letterTemplateId' is not null or undefined
            assertParamExists('archiveLetterTemplate', 'letterTemplateId', letterTemplateId)
            const localVarPath = `/api/v1/letter-templates/{letterTemplateId}/archive`
                .replace(`{${"letterTemplateId"}}`, encodeURIComponent(String(letterTemplateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Create a new letter template (requires letter_templates.manage permission). Templates start as DRAFT.
         * @summary Create letter template
         * @param {CreateLetterTemplateRequest} createLetterTemplateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createLetterTemplate: async (createLetterTemplateRequest: CreateLetterTemplateRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createLetterTemplateRequest' is not null or undefined
            assertParamExists('createLetterTemplate', 'createLetterTemplateRequest', createLetterTemplateRequest)
            const localVarPath = `/api/v1/letter-templates`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createLetterTemplateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a letter template by ID
         * @summary Get letter template by ID
         * @param {string} letterTemplateId Letter template UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLetterTemplateById: async (letterTemplateId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'letterTemplateId' is not null or undefined
            assertParamExists('getLetterTemplateById', 'letterTemplateId', letterTemplateId)
            const localVarPath = `/api/v1/letter-templates/{letterTemplateId}`
                .replace(`{${"letterTemplateId"}}`, encodeURIComponent(String(letterTemplateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the version history of the lineage the given template belongs to, newest version first, paginated.  The `letterTemplateId` may be any version id in the lineage (the current head or an older version) — the full lineage history is returned in all cases. Summaries are lightweight and do not include the template content; the full content of a specific version is available via `getLetterTemplateById`. 
         * @summary List version history of a letter template\'s lineage
         * @param {string} letterTemplateId Letter template UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listLetterTemplateVersions: async (letterTemplateId: string, page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'letterTemplateId' is not null or undefined
            assertParamExists('listLetterTemplateVersions', 'letterTemplateId', letterTemplateId)
            const localVarPath = `/api/v1/letter-templates/{letterTemplateId}/versions`
                .replace(`{${"letterTemplateId"}}`, encodeURIComponent(String(letterTemplateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the latest version of each template lineage for the authenticated user\'s organization. Editing an ACTIVE template creates a new version and archives the previous one; this endpoint returns only the head (latest version) of each lineage, never superseded versions.  `page`/`size` paginate over lineages (heads), not physical versions. The `status` filter applies to the head\'s status. 
         * @summary List letter templates
         * @param {LetterTemplateStatus} [status] Filter by template status
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listLetterTemplates: async (status?: LetterTemplateStatus, page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/letter-templates`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Renders template content as it currently stands in the editor, using the same renderer that produces the generated letter: A4 page box, Arial 11pt, and the margins of the organisation\'s letterhead when one is active and `useLetterhead` is on (2.5cm otherwise). This is the HTML the editor\'s inline example draws on its page; the printed letter, letterhead included, is `printLetterTemplateContent`.  Placeholder values are stand-ins from the placeholder registry, except `organization.*`, `behandelaar.*` and `date.*`, which are resolved for real — those need no client. Placeholders that cannot be resolved render as empty, exactly as they do in a generated letter, so a hole in the letter is visible before printing.  Requires `letter_templates.manage` permission. 
         * @summary Render unsaved template content through the letter renderer
         * @param {PreviewLetterTemplateContentRequest} previewLetterTemplateContentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewLetterTemplateContent: async (previewLetterTemplateContentRequest: PreviewLetterTemplateContentRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'previewLetterTemplateContentRequest' is not null or undefined
            assertParamExists('previewLetterTemplateContent', 'previewLetterTemplateContentRequest', previewLetterTemplateContentRequest)
            const localVarPath = `/api/v1/letter-templates/preview`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(previewLetterTemplateContentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Prints template content as it currently stands in the editor, through the same path that produces a generated letter: the configured letter renderer, the A4 page box, and — when the organisation has an active letterhead and `useLetterhead` is on — the letterhead\'s margins, with its first page laid under page 1 and its following page under the rest. Without a letterhead the pages are plain, with 2.5cm margins.  Placeholder values are those of `previewLetterTemplateContent`: stand-ins from the placeholder registry, with `organization.*`, `behandelaar.*` and `date.*` resolved for real.  Requires `letter_templates.manage` permission. 
         * @summary Print unsaved template content as the letter PDF
         * @param {PreviewLetterTemplateContentRequest} previewLetterTemplateContentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        printLetterTemplateContent: async (previewLetterTemplateContentRequest: PreviewLetterTemplateContentRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'previewLetterTemplateContentRequest' is not null or undefined
            assertParamExists('printLetterTemplateContent', 'previewLetterTemplateContentRequest', previewLetterTemplateContentRequest)
            const localVarPath = `/api/v1/letter-templates/preview/pdf`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/pdf,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(previewLetterTemplateContentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Update a letter template (requires letter_templates.manage permission).  If the template status is ACTIVE, a new version will be created and the original template will be archived. The response will contain the new version.  If the template status is DRAFT, it will be updated in place.  ARCHIVED templates cannot be updated. 
         * @summary Update letter template
         * @param {string} letterTemplateId Letter template UUID
         * @param {UpdateLetterTemplateRequest} updateLetterTemplateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateLetterTemplate: async (letterTemplateId: string, updateLetterTemplateRequest: UpdateLetterTemplateRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'letterTemplateId' is not null or undefined
            assertParamExists('updateLetterTemplate', 'letterTemplateId', letterTemplateId)
            // verify required parameter 'updateLetterTemplateRequest' is not null or undefined
            assertParamExists('updateLetterTemplate', 'updateLetterTemplateRequest', updateLetterTemplateRequest)
            const localVarPath = `/api/v1/letter-templates/{letterTemplateId}`
                .replace(`{${"letterTemplateId"}}`, encodeURIComponent(String(letterTemplateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateLetterTemplateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * LetterTemplatesApi - functional programming interface
 */
export const LetterTemplatesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = LetterTemplatesApiAxiosParamCreator(configuration)
    return {
        /**
         * Change template status from DRAFT to ACTIVE. Only DRAFT templates can be activated. 
         * @summary Activate letter template
         * @param {string} letterTemplateId Letter template UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activateLetterTemplate(letterTemplateId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LetterTemplateResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.activateLetterTemplate(letterTemplateId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LetterTemplatesApi.activateLetterTemplate']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Change template status to ARCHIVED. Both DRAFT and ACTIVE templates can be archived. Archived templates cannot be used for new letters. 
         * @summary Archive letter template
         * @param {string} letterTemplateId Letter template UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async archiveLetterTemplate(letterTemplateId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LetterTemplateResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.archiveLetterTemplate(letterTemplateId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LetterTemplatesApi.archiveLetterTemplate']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Create a new letter template (requires letter_templates.manage permission). Templates start as DRAFT.
         * @summary Create letter template
         * @param {CreateLetterTemplateRequest} createLetterTemplateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createLetterTemplate(createLetterTemplateRequest: CreateLetterTemplateRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LetterTemplateResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createLetterTemplate(createLetterTemplateRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LetterTemplatesApi.createLetterTemplate']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a letter template by ID
         * @summary Get letter template by ID
         * @param {string} letterTemplateId Letter template UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getLetterTemplateById(letterTemplateId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LetterTemplateResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getLetterTemplateById(letterTemplateId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LetterTemplatesApi.getLetterTemplateById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the version history of the lineage the given template belongs to, newest version first, paginated.  The `letterTemplateId` may be any version id in the lineage (the current head or an older version) — the full lineage history is returned in all cases. Summaries are lightweight and do not include the template content; the full content of a specific version is available via `getLetterTemplateById`. 
         * @summary List version history of a letter template\'s lineage
         * @param {string} letterTemplateId Letter template UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listLetterTemplateVersions(letterTemplateId: string, page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LetterTemplateVersionListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listLetterTemplateVersions(letterTemplateId, page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LetterTemplatesApi.listLetterTemplateVersions']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the latest version of each template lineage for the authenticated user\'s organization. Editing an ACTIVE template creates a new version and archives the previous one; this endpoint returns only the head (latest version) of each lineage, never superseded versions.  `page`/`size` paginate over lineages (heads), not physical versions. The `status` filter applies to the head\'s status. 
         * @summary List letter templates
         * @param {LetterTemplateStatus} [status] Filter by template status
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listLetterTemplates(status?: LetterTemplateStatus, page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LetterTemplateListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listLetterTemplates(status, page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LetterTemplatesApi.listLetterTemplates']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Renders template content as it currently stands in the editor, using the same renderer that produces the generated letter: A4 page box, Arial 11pt, and the margins of the organisation\'s letterhead when one is active and `useLetterhead` is on (2.5cm otherwise). This is the HTML the editor\'s inline example draws on its page; the printed letter, letterhead included, is `printLetterTemplateContent`.  Placeholder values are stand-ins from the placeholder registry, except `organization.*`, `behandelaar.*` and `date.*`, which are resolved for real — those need no client. Placeholders that cannot be resolved render as empty, exactly as they do in a generated letter, so a hole in the letter is visible before printing.  Requires `letter_templates.manage` permission. 
         * @summary Render unsaved template content through the letter renderer
         * @param {PreviewLetterTemplateContentRequest} previewLetterTemplateContentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async previewLetterTemplateContent(previewLetterTemplateContentRequest: PreviewLetterTemplateContentRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LetterTemplateContentPreviewResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.previewLetterTemplateContent(previewLetterTemplateContentRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LetterTemplatesApi.previewLetterTemplateContent']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Prints template content as it currently stands in the editor, through the same path that produces a generated letter: the configured letter renderer, the A4 page box, and — when the organisation has an active letterhead and `useLetterhead` is on — the letterhead\'s margins, with its first page laid under page 1 and its following page under the rest. Without a letterhead the pages are plain, with 2.5cm margins.  Placeholder values are those of `previewLetterTemplateContent`: stand-ins from the placeholder registry, with `organization.*`, `behandelaar.*` and `date.*` resolved for real.  Requires `letter_templates.manage` permission. 
         * @summary Print unsaved template content as the letter PDF
         * @param {PreviewLetterTemplateContentRequest} previewLetterTemplateContentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async printLetterTemplateContent(previewLetterTemplateContentRequest: PreviewLetterTemplateContentRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.printLetterTemplateContent(previewLetterTemplateContentRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LetterTemplatesApi.printLetterTemplateContent']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Update a letter template (requires letter_templates.manage permission).  If the template status is ACTIVE, a new version will be created and the original template will be archived. The response will contain the new version.  If the template status is DRAFT, it will be updated in place.  ARCHIVED templates cannot be updated. 
         * @summary Update letter template
         * @param {string} letterTemplateId Letter template UUID
         * @param {UpdateLetterTemplateRequest} updateLetterTemplateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateLetterTemplate(letterTemplateId: string, updateLetterTemplateRequest: UpdateLetterTemplateRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LetterTemplateResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateLetterTemplate(letterTemplateId, updateLetterTemplateRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LetterTemplatesApi.updateLetterTemplate']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * LetterTemplatesApi - factory interface
 */
export const LetterTemplatesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = LetterTemplatesApiFp(configuration)
    return {
        /**
         * Change template status from DRAFT to ACTIVE. Only DRAFT templates can be activated. 
         * @summary Activate letter template
         * @param {string} letterTemplateId Letter template UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activateLetterTemplate(letterTemplateId: string, options?: RawAxiosRequestConfig): AxiosPromise<LetterTemplateResponse> {
            return localVarFp.activateLetterTemplate(letterTemplateId, options).then((request) => request(axios, basePath));
        },
        /**
         * Change template status to ARCHIVED. Both DRAFT and ACTIVE templates can be archived. Archived templates cannot be used for new letters. 
         * @summary Archive letter template
         * @param {string} letterTemplateId Letter template UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        archiveLetterTemplate(letterTemplateId: string, options?: RawAxiosRequestConfig): AxiosPromise<LetterTemplateResponse> {
            return localVarFp.archiveLetterTemplate(letterTemplateId, options).then((request) => request(axios, basePath));
        },
        /**
         * Create a new letter template (requires letter_templates.manage permission). Templates start as DRAFT.
         * @summary Create letter template
         * @param {CreateLetterTemplateRequest} createLetterTemplateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createLetterTemplate(createLetterTemplateRequest: CreateLetterTemplateRequest, options?: RawAxiosRequestConfig): AxiosPromise<LetterTemplateResponse> {
            return localVarFp.createLetterTemplate(createLetterTemplateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a letter template by ID
         * @summary Get letter template by ID
         * @param {string} letterTemplateId Letter template UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLetterTemplateById(letterTemplateId: string, options?: RawAxiosRequestConfig): AxiosPromise<LetterTemplateResponse> {
            return localVarFp.getLetterTemplateById(letterTemplateId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the version history of the lineage the given template belongs to, newest version first, paginated.  The `letterTemplateId` may be any version id in the lineage (the current head or an older version) — the full lineage history is returned in all cases. Summaries are lightweight and do not include the template content; the full content of a specific version is available via `getLetterTemplateById`. 
         * @summary List version history of a letter template\'s lineage
         * @param {string} letterTemplateId Letter template UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listLetterTemplateVersions(letterTemplateId: string, page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<LetterTemplateVersionListResponse> {
            return localVarFp.listLetterTemplateVersions(letterTemplateId, page, size, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the latest version of each template lineage for the authenticated user\'s organization. Editing an ACTIVE template creates a new version and archives the previous one; this endpoint returns only the head (latest version) of each lineage, never superseded versions.  `page`/`size` paginate over lineages (heads), not physical versions. The `status` filter applies to the head\'s status. 
         * @summary List letter templates
         * @param {LetterTemplateStatus} [status] Filter by template status
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listLetterTemplates(status?: LetterTemplateStatus, page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<LetterTemplateListResponse> {
            return localVarFp.listLetterTemplates(status, page, size, options).then((request) => request(axios, basePath));
        },
        /**
         * Renders template content as it currently stands in the editor, using the same renderer that produces the generated letter: A4 page box, Arial 11pt, and the margins of the organisation\'s letterhead when one is active and `useLetterhead` is on (2.5cm otherwise). This is the HTML the editor\'s inline example draws on its page; the printed letter, letterhead included, is `printLetterTemplateContent`.  Placeholder values are stand-ins from the placeholder registry, except `organization.*`, `behandelaar.*` and `date.*`, which are resolved for real — those need no client. Placeholders that cannot be resolved render as empty, exactly as they do in a generated letter, so a hole in the letter is visible before printing.  Requires `letter_templates.manage` permission. 
         * @summary Render unsaved template content through the letter renderer
         * @param {PreviewLetterTemplateContentRequest} previewLetterTemplateContentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewLetterTemplateContent(previewLetterTemplateContentRequest: PreviewLetterTemplateContentRequest, options?: RawAxiosRequestConfig): AxiosPromise<LetterTemplateContentPreviewResponse> {
            return localVarFp.previewLetterTemplateContent(previewLetterTemplateContentRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Prints template content as it currently stands in the editor, through the same path that produces a generated letter: the configured letter renderer, the A4 page box, and — when the organisation has an active letterhead and `useLetterhead` is on — the letterhead\'s margins, with its first page laid under page 1 and its following page under the rest. Without a letterhead the pages are plain, with 2.5cm margins.  Placeholder values are those of `previewLetterTemplateContent`: stand-ins from the placeholder registry, with `organization.*`, `behandelaar.*` and `date.*` resolved for real.  Requires `letter_templates.manage` permission. 
         * @summary Print unsaved template content as the letter PDF
         * @param {PreviewLetterTemplateContentRequest} previewLetterTemplateContentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        printLetterTemplateContent(previewLetterTemplateContentRequest: PreviewLetterTemplateContentRequest, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.printLetterTemplateContent(previewLetterTemplateContentRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Update a letter template (requires letter_templates.manage permission).  If the template status is ACTIVE, a new version will be created and the original template will be archived. The response will contain the new version.  If the template status is DRAFT, it will be updated in place.  ARCHIVED templates cannot be updated. 
         * @summary Update letter template
         * @param {string} letterTemplateId Letter template UUID
         * @param {UpdateLetterTemplateRequest} updateLetterTemplateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateLetterTemplate(letterTemplateId: string, updateLetterTemplateRequest: UpdateLetterTemplateRequest, options?: RawAxiosRequestConfig): AxiosPromise<LetterTemplateResponse> {
            return localVarFp.updateLetterTemplate(letterTemplateId, updateLetterTemplateRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * LetterTemplatesApi - object-oriented interface
 */
export class LetterTemplatesApi extends BaseAPI {
    /**
     * Change template status from DRAFT to ACTIVE. Only DRAFT templates can be activated. 
     * @summary Activate letter template
     * @param {string} letterTemplateId Letter template UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public activateLetterTemplate(letterTemplateId: string, options?: RawAxiosRequestConfig) {
        return LetterTemplatesApiFp(this.configuration).activateLetterTemplate(letterTemplateId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Change template status to ARCHIVED. Both DRAFT and ACTIVE templates can be archived. Archived templates cannot be used for new letters. 
     * @summary Archive letter template
     * @param {string} letterTemplateId Letter template UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public archiveLetterTemplate(letterTemplateId: string, options?: RawAxiosRequestConfig) {
        return LetterTemplatesApiFp(this.configuration).archiveLetterTemplate(letterTemplateId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Create a new letter template (requires letter_templates.manage permission). Templates start as DRAFT.
     * @summary Create letter template
     * @param {CreateLetterTemplateRequest} createLetterTemplateRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createLetterTemplate(createLetterTemplateRequest: CreateLetterTemplateRequest, options?: RawAxiosRequestConfig) {
        return LetterTemplatesApiFp(this.configuration).createLetterTemplate(createLetterTemplateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a letter template by ID
     * @summary Get letter template by ID
     * @param {string} letterTemplateId Letter template UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getLetterTemplateById(letterTemplateId: string, options?: RawAxiosRequestConfig) {
        return LetterTemplatesApiFp(this.configuration).getLetterTemplateById(letterTemplateId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the version history of the lineage the given template belongs to, newest version first, paginated.  The `letterTemplateId` may be any version id in the lineage (the current head or an older version) — the full lineage history is returned in all cases. Summaries are lightweight and do not include the template content; the full content of a specific version is available via `getLetterTemplateById`. 
     * @summary List version history of a letter template\'s lineage
     * @param {string} letterTemplateId Letter template UUID
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of items per page
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listLetterTemplateVersions(letterTemplateId: string, page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return LetterTemplatesApiFp(this.configuration).listLetterTemplateVersions(letterTemplateId, page, size, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the latest version of each template lineage for the authenticated user\'s organization. Editing an ACTIVE template creates a new version and archives the previous one; this endpoint returns only the head (latest version) of each lineage, never superseded versions.  `page`/`size` paginate over lineages (heads), not physical versions. The `status` filter applies to the head\'s status. 
     * @summary List letter templates
     * @param {LetterTemplateStatus} [status] Filter by template status
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of items per page
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listLetterTemplates(status?: LetterTemplateStatus, page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return LetterTemplatesApiFp(this.configuration).listLetterTemplates(status, page, size, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Renders template content as it currently stands in the editor, using the same renderer that produces the generated letter: A4 page box, Arial 11pt, and the margins of the organisation\'s letterhead when one is active and `useLetterhead` is on (2.5cm otherwise). This is the HTML the editor\'s inline example draws on its page; the printed letter, letterhead included, is `printLetterTemplateContent`.  Placeholder values are stand-ins from the placeholder registry, except `organization.*`, `behandelaar.*` and `date.*`, which are resolved for real — those need no client. Placeholders that cannot be resolved render as empty, exactly as they do in a generated letter, so a hole in the letter is visible before printing.  Requires `letter_templates.manage` permission. 
     * @summary Render unsaved template content through the letter renderer
     * @param {PreviewLetterTemplateContentRequest} previewLetterTemplateContentRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public previewLetterTemplateContent(previewLetterTemplateContentRequest: PreviewLetterTemplateContentRequest, options?: RawAxiosRequestConfig) {
        return LetterTemplatesApiFp(this.configuration).previewLetterTemplateContent(previewLetterTemplateContentRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Prints template content as it currently stands in the editor, through the same path that produces a generated letter: the configured letter renderer, the A4 page box, and — when the organisation has an active letterhead and `useLetterhead` is on — the letterhead\'s margins, with its first page laid under page 1 and its following page under the rest. Without a letterhead the pages are plain, with 2.5cm margins.  Placeholder values are those of `previewLetterTemplateContent`: stand-ins from the placeholder registry, with `organization.*`, `behandelaar.*` and `date.*` resolved for real.  Requires `letter_templates.manage` permission. 
     * @summary Print unsaved template content as the letter PDF
     * @param {PreviewLetterTemplateContentRequest} previewLetterTemplateContentRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public printLetterTemplateContent(previewLetterTemplateContentRequest: PreviewLetterTemplateContentRequest, options?: RawAxiosRequestConfig) {
        return LetterTemplatesApiFp(this.configuration).printLetterTemplateContent(previewLetterTemplateContentRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Update a letter template (requires letter_templates.manage permission).  If the template status is ACTIVE, a new version will be created and the original template will be archived. The response will contain the new version.  If the template status is DRAFT, it will be updated in place.  ARCHIVED templates cannot be updated. 
     * @summary Update letter template
     * @param {string} letterTemplateId Letter template UUID
     * @param {UpdateLetterTemplateRequest} updateLetterTemplateRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateLetterTemplate(letterTemplateId: string, updateLetterTemplateRequest: UpdateLetterTemplateRequest, options?: RawAxiosRequestConfig) {
        return LetterTemplatesApiFp(this.configuration).updateLetterTemplate(letterTemplateId, updateLetterTemplateRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * LetterheadApi - axios parameter creator
 */
export const LetterheadApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * What a letter editor needs to draw the page as it will be printed (ADR-048 §3): the letter stylesheet the renderer inlines, with the page margins it applies, and whether the page is the organisation\'s active letterhead. The letterhead\'s own pages are at `getLetterheadPage`.  Requires `letter_templates.manage` or `letters.generate` permission. 
         * @summary Get the page a letter is written on
         * @param {boolean} useLetterhead The template\&#39;s letterhead setting; false is a plain page whatever the organisation has
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLetterPage: async (useLetterhead: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'useLetterhead' is not null or undefined
            assertParamExists('getLetterPage', 'useLetterhead', useLetterhead)
            const localVarPath = `/api/v1/letters/page`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (useLetterhead !== undefined) {
                localVarQueryParameter['useLetterhead'] = useLetterhead;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The letterhead of the current organisation, active or not. `404` when none was uploaded.  Requires `organization.settings.update` or `letter_templates.manage` permission. 
         * @summary Get the organisation\'s letterhead
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLetterhead: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/organizations/me/letterhead`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The stored PDF for the first page or the following pages, for drawing a preview and the page a letter is edited on.  Requires `organization.settings.update`, `letter_templates.manage` or `letters.generate` permission. 
         * @summary Download one page of the letterhead
         * @param {LetterheadPage} page 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLetterheadPage: async (page: LetterheadPage, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'page' is not null or undefined
            assertParamExists('getLetterheadPage', 'page', page)
            const localVarPath = `/api/v1/organizations/me/letterhead/pages/{page}`
                .replace(`{${"page"}}`, encodeURIComponent(String(page)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/pdf,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Only the fields sent are changed. A deactivated letterhead stays stored, and letters are printed without it until it is activated again.  Requires `organization.settings.update` permission. 
         * @summary Change the letterhead\'s margins, or (de)activate it
         * @param {UpdateLetterheadRequest} updateLetterheadRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateLetterhead: async (updateLetterheadRequest: UpdateLetterheadRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'updateLetterheadRequest' is not null or undefined
            assertParamExists('updateLetterhead', 'updateLetterheadRequest', updateLetterheadRequest)
            const localVarPath = `/api/v1/organizations/me/letterhead`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateLetterheadRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Stores the letterhead PDFs and its margins, and makes it active. Replaces any letterhead the organisation had: a following-page PDF that is left out is removed, so pages 2 and on are printed plain. Letters already generated keep the letterhead they were printed on.  Each PDF must be a single A4 page. It is scanned for malware before it is stored, and a file that is not a PDF, not A4 or more than one page is refused with a Dutch reason.  Requires `organization.settings.update` permission. 
         * @summary Upload or replace the organisation\'s letterhead
         * @param {File} firstPage The letterhead under page 1 — a one-page A4 PDF, max 5 MB
         * @param {number} marginTopMm Space the letter body keeps clear at the top of every page, in mm
         * @param {number} marginBottomMm Space the letter body keeps clear at the bottom of every page, in mm
         * @param {File} [followingPage] The letterhead under pages 2 and on. Absent means those pages are plain.
         * @param {number | null} [marginLeftMm] Left margin in mm. Absent means 25, the margin of a letter without letterhead.
         * @param {number | null} [marginRightMm] Right margin in mm. Absent means 25, the margin of a letter without letterhead.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadLetterhead: async (firstPage: File, marginTopMm: number, marginBottomMm: number, followingPage?: File, marginLeftMm?: number | null, marginRightMm?: number | null, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'firstPage' is not null or undefined
            assertParamExists('uploadLetterhead', 'firstPage', firstPage)
            // verify required parameter 'marginTopMm' is not null or undefined
            assertParamExists('uploadLetterhead', 'marginTopMm', marginTopMm)
            // verify required parameter 'marginBottomMm' is not null or undefined
            assertParamExists('uploadLetterhead', 'marginBottomMm', marginBottomMm)
            const localVarPath = `/api/v1/organizations/me/letterhead`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (firstPage !== undefined) { 
                localVarFormParams.append('firstPage', firstPage as any);
            }

            if (followingPage !== undefined) { 
                localVarFormParams.append('followingPage', followingPage as any);
            }

            if (marginTopMm !== undefined) { 
                localVarFormParams.append('marginTopMm', marginTopMm as any);
            }

            if (marginBottomMm !== undefined) { 
                localVarFormParams.append('marginBottomMm', marginBottomMm as any);
            }

            if (marginLeftMm !== undefined) { 
                localVarFormParams.append('marginLeftMm', marginLeftMm as any);
            }

            if (marginRightMm !== undefined) { 
                localVarFormParams.append('marginRightMm', marginRightMm as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * LetterheadApi - functional programming interface
 */
export const LetterheadApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = LetterheadApiAxiosParamCreator(configuration)
    return {
        /**
         * What a letter editor needs to draw the page as it will be printed (ADR-048 §3): the letter stylesheet the renderer inlines, with the page margins it applies, and whether the page is the organisation\'s active letterhead. The letterhead\'s own pages are at `getLetterheadPage`.  Requires `letter_templates.manage` or `letters.generate` permission. 
         * @summary Get the page a letter is written on
         * @param {boolean} useLetterhead The template\&#39;s letterhead setting; false is a plain page whatever the organisation has
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getLetterPage(useLetterhead: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LetterPageResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getLetterPage(useLetterhead, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LetterheadApi.getLetterPage']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The letterhead of the current organisation, active or not. `404` when none was uploaded.  Requires `organization.settings.update` or `letter_templates.manage` permission. 
         * @summary Get the organisation\'s letterhead
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getLetterhead(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LetterheadResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getLetterhead(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LetterheadApi.getLetterhead']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The stored PDF for the first page or the following pages, for drawing a preview and the page a letter is edited on.  Requires `organization.settings.update`, `letter_templates.manage` or `letters.generate` permission. 
         * @summary Download one page of the letterhead
         * @param {LetterheadPage} page 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getLetterheadPage(page: LetterheadPage, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getLetterheadPage(page, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LetterheadApi.getLetterheadPage']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Only the fields sent are changed. A deactivated letterhead stays stored, and letters are printed without it until it is activated again.  Requires `organization.settings.update` permission. 
         * @summary Change the letterhead\'s margins, or (de)activate it
         * @param {UpdateLetterheadRequest} updateLetterheadRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateLetterhead(updateLetterheadRequest: UpdateLetterheadRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LetterheadResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateLetterhead(updateLetterheadRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LetterheadApi.updateLetterhead']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Stores the letterhead PDFs and its margins, and makes it active. Replaces any letterhead the organisation had: a following-page PDF that is left out is removed, so pages 2 and on are printed plain. Letters already generated keep the letterhead they were printed on.  Each PDF must be a single A4 page. It is scanned for malware before it is stored, and a file that is not a PDF, not A4 or more than one page is refused with a Dutch reason.  Requires `organization.settings.update` permission. 
         * @summary Upload or replace the organisation\'s letterhead
         * @param {File} firstPage The letterhead under page 1 — a one-page A4 PDF, max 5 MB
         * @param {number} marginTopMm Space the letter body keeps clear at the top of every page, in mm
         * @param {number} marginBottomMm Space the letter body keeps clear at the bottom of every page, in mm
         * @param {File} [followingPage] The letterhead under pages 2 and on. Absent means those pages are plain.
         * @param {number | null} [marginLeftMm] Left margin in mm. Absent means 25, the margin of a letter without letterhead.
         * @param {number | null} [marginRightMm] Right margin in mm. Absent means 25, the margin of a letter without letterhead.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async uploadLetterhead(firstPage: File, marginTopMm: number, marginBottomMm: number, followingPage?: File, marginLeftMm?: number | null, marginRightMm?: number | null, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LetterheadResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.uploadLetterhead(firstPage, marginTopMm, marginBottomMm, followingPage, marginLeftMm, marginRightMm, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LetterheadApi.uploadLetterhead']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * LetterheadApi - factory interface
 */
export const LetterheadApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = LetterheadApiFp(configuration)
    return {
        /**
         * What a letter editor needs to draw the page as it will be printed (ADR-048 §3): the letter stylesheet the renderer inlines, with the page margins it applies, and whether the page is the organisation\'s active letterhead. The letterhead\'s own pages are at `getLetterheadPage`.  Requires `letter_templates.manage` or `letters.generate` permission. 
         * @summary Get the page a letter is written on
         * @param {boolean} useLetterhead The template\&#39;s letterhead setting; false is a plain page whatever the organisation has
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLetterPage(useLetterhead: boolean, options?: RawAxiosRequestConfig): AxiosPromise<LetterPageResponse> {
            return localVarFp.getLetterPage(useLetterhead, options).then((request) => request(axios, basePath));
        },
        /**
         * The letterhead of the current organisation, active or not. `404` when none was uploaded.  Requires `organization.settings.update` or `letter_templates.manage` permission. 
         * @summary Get the organisation\'s letterhead
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLetterhead(options?: RawAxiosRequestConfig): AxiosPromise<LetterheadResponse> {
            return localVarFp.getLetterhead(options).then((request) => request(axios, basePath));
        },
        /**
         * The stored PDF for the first page or the following pages, for drawing a preview and the page a letter is edited on.  Requires `organization.settings.update`, `letter_templates.manage` or `letters.generate` permission. 
         * @summary Download one page of the letterhead
         * @param {LetterheadPage} page 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getLetterheadPage(page: LetterheadPage, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.getLetterheadPage(page, options).then((request) => request(axios, basePath));
        },
        /**
         * Only the fields sent are changed. A deactivated letterhead stays stored, and letters are printed without it until it is activated again.  Requires `organization.settings.update` permission. 
         * @summary Change the letterhead\'s margins, or (de)activate it
         * @param {UpdateLetterheadRequest} updateLetterheadRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateLetterhead(updateLetterheadRequest: UpdateLetterheadRequest, options?: RawAxiosRequestConfig): AxiosPromise<LetterheadResponse> {
            return localVarFp.updateLetterhead(updateLetterheadRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Stores the letterhead PDFs and its margins, and makes it active. Replaces any letterhead the organisation had: a following-page PDF that is left out is removed, so pages 2 and on are printed plain. Letters already generated keep the letterhead they were printed on.  Each PDF must be a single A4 page. It is scanned for malware before it is stored, and a file that is not a PDF, not A4 or more than one page is refused with a Dutch reason.  Requires `organization.settings.update` permission. 
         * @summary Upload or replace the organisation\'s letterhead
         * @param {File} firstPage The letterhead under page 1 — a one-page A4 PDF, max 5 MB
         * @param {number} marginTopMm Space the letter body keeps clear at the top of every page, in mm
         * @param {number} marginBottomMm Space the letter body keeps clear at the bottom of every page, in mm
         * @param {File} [followingPage] The letterhead under pages 2 and on. Absent means those pages are plain.
         * @param {number | null} [marginLeftMm] Left margin in mm. Absent means 25, the margin of a letter without letterhead.
         * @param {number | null} [marginRightMm] Right margin in mm. Absent means 25, the margin of a letter without letterhead.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadLetterhead(firstPage: File, marginTopMm: number, marginBottomMm: number, followingPage?: File, marginLeftMm?: number | null, marginRightMm?: number | null, options?: RawAxiosRequestConfig): AxiosPromise<LetterheadResponse> {
            return localVarFp.uploadLetterhead(firstPage, marginTopMm, marginBottomMm, followingPage, marginLeftMm, marginRightMm, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * LetterheadApi - object-oriented interface
 */
export class LetterheadApi extends BaseAPI {
    /**
     * What a letter editor needs to draw the page as it will be printed (ADR-048 §3): the letter stylesheet the renderer inlines, with the page margins it applies, and whether the page is the organisation\'s active letterhead. The letterhead\'s own pages are at `getLetterheadPage`.  Requires `letter_templates.manage` or `letters.generate` permission. 
     * @summary Get the page a letter is written on
     * @param {boolean} useLetterhead The template\&#39;s letterhead setting; false is a plain page whatever the organisation has
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getLetterPage(useLetterhead: boolean, options?: RawAxiosRequestConfig) {
        return LetterheadApiFp(this.configuration).getLetterPage(useLetterhead, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The letterhead of the current organisation, active or not. `404` when none was uploaded.  Requires `organization.settings.update` or `letter_templates.manage` permission. 
     * @summary Get the organisation\'s letterhead
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getLetterhead(options?: RawAxiosRequestConfig) {
        return LetterheadApiFp(this.configuration).getLetterhead(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The stored PDF for the first page or the following pages, for drawing a preview and the page a letter is edited on.  Requires `organization.settings.update`, `letter_templates.manage` or `letters.generate` permission. 
     * @summary Download one page of the letterhead
     * @param {LetterheadPage} page 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getLetterheadPage(page: LetterheadPage, options?: RawAxiosRequestConfig) {
        return LetterheadApiFp(this.configuration).getLetterheadPage(page, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Only the fields sent are changed. A deactivated letterhead stays stored, and letters are printed without it until it is activated again.  Requires `organization.settings.update` permission. 
     * @summary Change the letterhead\'s margins, or (de)activate it
     * @param {UpdateLetterheadRequest} updateLetterheadRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateLetterhead(updateLetterheadRequest: UpdateLetterheadRequest, options?: RawAxiosRequestConfig) {
        return LetterheadApiFp(this.configuration).updateLetterhead(updateLetterheadRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Stores the letterhead PDFs and its margins, and makes it active. Replaces any letterhead the organisation had: a following-page PDF that is left out is removed, so pages 2 and on are printed plain. Letters already generated keep the letterhead they were printed on.  Each PDF must be a single A4 page. It is scanned for malware before it is stored, and a file that is not a PDF, not A4 or more than one page is refused with a Dutch reason.  Requires `organization.settings.update` permission. 
     * @summary Upload or replace the organisation\'s letterhead
     * @param {File} firstPage The letterhead under page 1 — a one-page A4 PDF, max 5 MB
     * @param {number} marginTopMm Space the letter body keeps clear at the top of every page, in mm
     * @param {number} marginBottomMm Space the letter body keeps clear at the bottom of every page, in mm
     * @param {File} [followingPage] The letterhead under pages 2 and on. Absent means those pages are plain.
     * @param {number | null} [marginLeftMm] Left margin in mm. Absent means 25, the margin of a letter without letterhead.
     * @param {number | null} [marginRightMm] Right margin in mm. Absent means 25, the margin of a letter without letterhead.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public uploadLetterhead(firstPage: File, marginTopMm: number, marginBottomMm: number, followingPage?: File, marginLeftMm?: number | null, marginRightMm?: number | null, options?: RawAxiosRequestConfig) {
        return LetterheadApiFp(this.configuration).uploadLetterhead(firstPage, marginTopMm, marginBottomMm, followingPage, marginLeftMm, marginRightMm, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * LettersApi - axios parameter creator
 */
export const LettersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Deletes a generated letter and its associated document from the client\'s dossier. This is a soft delete - the document is marked as deleted but not permanently removed.  Requires `documents.delete` permission. 
         * @summary Delete a generated letter
         * @param {string} clientId Client UUID
         * @param {string} letterId Generated letter UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteClientLetter: async (clientId: string, letterId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('deleteClientLetter', 'clientId', clientId)
            // verify required parameter 'letterId' is not null or undefined
            assertParamExists('deleteClientLetter', 'letterId', letterId)
            const localVarPath = `/api/v1/clients/{clientId}/letters/{letterId}`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"letterId"}}`, encodeURIComponent(String(letterId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Generate a letter PDF from a template for a specific client.  The PDF is saved as a document in the client\'s dossier and a GeneratedLetter record is created for audit purposes. The response includes the letter and document IDs for future reference.  Requires `letters.generate` permission. 
         * @summary Generate a letter PDF
         * @param {GenerateLetterRequest} generateLetterRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        generateLetter: async (generateLetterRequest: GenerateLetterRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'generateLetterRequest' is not null or undefined
            assertParamExists('generateLetter', 'generateLetterRequest', generateLetterRequest)
            const localVarPath = `/api/v1/letters/generate`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(generateLetterRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the HTML a generated letter\'s PDF was rendered from, including any edits made before generating, so it can be reopened in the letter editor and generated again as a corrected letter. The HTML is returned as stored; it was sanitized when the letter was generated. A letter generated before its content was stored has none to return — see `editable` on `GeneratedLetterSummary` — and answers 404.  Requires `letters.generate` permission and access to the client. 
         * @summary Get a generated letter\'s stored content
         * @param {string} clientId Client UUID
         * @param {string} letterId Generated letter UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientLetter: async (clientId: string, letterId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('getClientLetter', 'clientId', clientId)
            // verify required parameter 'letterId' is not null or undefined
            assertParamExists('getClientLetter', 'letterId', letterId)
            const localVarPath = `/api/v1/clients/{clientId}/letters/{letterId}`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"letterId"}}`, encodeURIComponent(String(letterId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns one page of the generated letters for a specific client, newest first. Each letter includes the template name, generation date, and the user who generated it.  Requires `letters.generate` permission. 
         * @summary List generated letters for a client
         * @param {string} clientId Client UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of letters per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientLetters: async (clientId: string, page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('listClientLetters', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/letters`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Generates a preview of a letter with placeholders resolved. Returns rendered HTML (not PDF) and a list of any missing or empty placeholders.  This endpoint is used to show users what the letter will look like before generating the final PDF, and to warn them about any missing data.  Requires `letters.generate` permission. 
         * @summary Preview letter with resolved placeholders
         * @param {LetterPreviewRequest} letterPreviewRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewLetter: async (letterPreviewRequest: LetterPreviewRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'letterPreviewRequest' is not null or undefined
            assertParamExists('previewLetter', 'letterPreviewRequest', letterPreviewRequest)
            const localVarPath = `/api/v1/letters/preview`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(letterPreviewRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Sends a previously generated letter\'s PDF to an external recipient through the organization\'s Zivver secure-email gateway (ADR-028).  The caller must have enabled Zivver on their profile (giving them a validated sender address) and the organization must have Zivver SMTP configured. The per-message recipient verification policy is derived from `verificationMethod` and the optional `verificationDetails`.  Requires a FULL license and the `letters.generate` permission. 
         * @summary Send a generated letter to a recipient via Zivver secure email
         * @param {string} clientId Client UUID
         * @param {string} letterId Generated letter UUID
         * @param {SendLetterViaZivverRequest} sendLetterViaZivverRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendLetterViaZivver: async (clientId: string, letterId: string, sendLetterViaZivverRequest: SendLetterViaZivverRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('sendLetterViaZivver', 'clientId', clientId)
            // verify required parameter 'letterId' is not null or undefined
            assertParamExists('sendLetterViaZivver', 'letterId', letterId)
            // verify required parameter 'sendLetterViaZivverRequest' is not null or undefined
            assertParamExists('sendLetterViaZivver', 'sendLetterViaZivverRequest', sendLetterViaZivverRequest)
            const localVarPath = `/api/v1/clients/{clientId}/letters/{letterId}/send-via-zivver`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"letterId"}}`, encodeURIComponent(String(letterId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(sendLetterViaZivverRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Renders a generated letter as a ZorgMail **MEDVRI** and hands it to Enovation\'s `edi-smtp` gateway, so it lands in the huisarts\'s own EPD filed against the right patient (#4121, ADR-041). This is the act the whole ZorgMail epic exists for.  **The recipient is stated, never derived.** `recipient` is what the sender confirmed — normally a candidate from `getZorgMailRecipient`, possibly one they corrected. The API will not resolve a huisarts on its own: the chain from zorgtraject to verwijzer to AGB code to mailbox can be wrong in ways only a person notices, and a letter sent to last year\'s verwijzer is a disclosure to the wrong care provider.  **A 200 means accepted, not delivered.** ZorgMail acknowledges the submission and validates the content afterwards; a content failure bounces asynchronously into the *practice\'s* own ZorgMail mailbox, which Scrivio does not read until #4122. The response is named accordingly, and the UI must be too (ADR-041 §7).  **Every outcome is durable.** Accepted or refused, the attempt is recorded and shown on the letter afterwards (`zorgMailSends` on `GeneratedLetterSummary`); a refusal carries the row\'s `sendId` on the problem detail. A letter to a huisarts that did not arrive is a clinical problem, so the outcome may not be a toast that disappears on the next navigation.  Gates, all server-side because frontend hiding is best-effort: a FULL license, the `letters.generate` permission, ZorgMail wired in this deployment, and the practice\'s ZorgMail Account-ID recorded (`getZorgMailConfig`). There is deliberately **no per-user enablement** — unlike Zivver, a ZorgMail afzender is a name in the message payload with nothing to verify against, so any behandelaar of a configured practice can send (ADR-041 §3). 
         * @summary Send a generated letter to the huisarts as a MEDVRI over ZorgMail
         * @param {string} clientId Client UUID
         * @param {string} letterId Generated letter UUID
         * @param {SendLetterViaZorgMailRequest} sendLetterViaZorgMailRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendLetterViaZorgMail: async (clientId: string, letterId: string, sendLetterViaZorgMailRequest: SendLetterViaZorgMailRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('sendLetterViaZorgMail', 'clientId', clientId)
            // verify required parameter 'letterId' is not null or undefined
            assertParamExists('sendLetterViaZorgMail', 'letterId', letterId)
            // verify required parameter 'sendLetterViaZorgMailRequest' is not null or undefined
            assertParamExists('sendLetterViaZorgMail', 'sendLetterViaZorgMailRequest', sendLetterViaZorgMailRequest)
            const localVarPath = `/api/v1/clients/{clientId}/letters/{letterId}/send-via-zorgmail`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"letterId"}}`, encodeURIComponent(String(letterId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(sendLetterViaZorgMailRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * LettersApi - functional programming interface
 */
export const LettersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = LettersApiAxiosParamCreator(configuration)
    return {
        /**
         * Deletes a generated letter and its associated document from the client\'s dossier. This is a soft delete - the document is marked as deleted but not permanently removed.  Requires `documents.delete` permission. 
         * @summary Delete a generated letter
         * @param {string} clientId Client UUID
         * @param {string} letterId Generated letter UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteClientLetter(clientId: string, letterId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteClientLetter(clientId, letterId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LettersApi.deleteClientLetter']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Generate a letter PDF from a template for a specific client.  The PDF is saved as a document in the client\'s dossier and a GeneratedLetter record is created for audit purposes. The response includes the letter and document IDs for future reference.  Requires `letters.generate` permission. 
         * @summary Generate a letter PDF
         * @param {GenerateLetterRequest} generateLetterRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async generateLetter(generateLetterRequest: GenerateLetterRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GenerateLetterResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.generateLetter(generateLetterRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LettersApi.generateLetter']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the HTML a generated letter\'s PDF was rendered from, including any edits made before generating, so it can be reopened in the letter editor and generated again as a corrected letter. The HTML is returned as stored; it was sanitized when the letter was generated. A letter generated before its content was stored has none to return — see `editable` on `GeneratedLetterSummary` — and answers 404.  Requires `letters.generate` permission and access to the client. 
         * @summary Get a generated letter\'s stored content
         * @param {string} clientId Client UUID
         * @param {string} letterId Generated letter UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getClientLetter(clientId: string, letterId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GeneratedLetterContent>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getClientLetter(clientId, letterId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LettersApi.getClientLetter']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns one page of the generated letters for a specific client, newest first. Each letter includes the template name, generation date, and the user who generated it.  Requires `letters.generate` permission. 
         * @summary List generated letters for a client
         * @param {string} clientId Client UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of letters per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listClientLetters(clientId: string, page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<GeneratedLetterListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listClientLetters(clientId, page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LettersApi.listClientLetters']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Generates a preview of a letter with placeholders resolved. Returns rendered HTML (not PDF) and a list of any missing or empty placeholders.  This endpoint is used to show users what the letter will look like before generating the final PDF, and to warn them about any missing data.  Requires `letters.generate` permission. 
         * @summary Preview letter with resolved placeholders
         * @param {LetterPreviewRequest} letterPreviewRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async previewLetter(letterPreviewRequest: LetterPreviewRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<LetterPreviewResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.previewLetter(letterPreviewRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LettersApi.previewLetter']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Sends a previously generated letter\'s PDF to an external recipient through the organization\'s Zivver secure-email gateway (ADR-028).  The caller must have enabled Zivver on their profile (giving them a validated sender address) and the organization must have Zivver SMTP configured. The per-message recipient verification policy is derived from `verificationMethod` and the optional `verificationDetails`.  Requires a FULL license and the `letters.generate` permission. 
         * @summary Send a generated letter to a recipient via Zivver secure email
         * @param {string} clientId Client UUID
         * @param {string} letterId Generated letter UUID
         * @param {SendLetterViaZivverRequest} sendLetterViaZivverRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async sendLetterViaZivver(clientId: string, letterId: string, sendLetterViaZivverRequest: SendLetterViaZivverRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SendLetterViaZivverResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.sendLetterViaZivver(clientId, letterId, sendLetterViaZivverRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LettersApi.sendLetterViaZivver']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Renders a generated letter as a ZorgMail **MEDVRI** and hands it to Enovation\'s `edi-smtp` gateway, so it lands in the huisarts\'s own EPD filed against the right patient (#4121, ADR-041). This is the act the whole ZorgMail epic exists for.  **The recipient is stated, never derived.** `recipient` is what the sender confirmed — normally a candidate from `getZorgMailRecipient`, possibly one they corrected. The API will not resolve a huisarts on its own: the chain from zorgtraject to verwijzer to AGB code to mailbox can be wrong in ways only a person notices, and a letter sent to last year\'s verwijzer is a disclosure to the wrong care provider.  **A 200 means accepted, not delivered.** ZorgMail acknowledges the submission and validates the content afterwards; a content failure bounces asynchronously into the *practice\'s* own ZorgMail mailbox, which Scrivio does not read until #4122. The response is named accordingly, and the UI must be too (ADR-041 §7).  **Every outcome is durable.** Accepted or refused, the attempt is recorded and shown on the letter afterwards (`zorgMailSends` on `GeneratedLetterSummary`); a refusal carries the row\'s `sendId` on the problem detail. A letter to a huisarts that did not arrive is a clinical problem, so the outcome may not be a toast that disappears on the next navigation.  Gates, all server-side because frontend hiding is best-effort: a FULL license, the `letters.generate` permission, ZorgMail wired in this deployment, and the practice\'s ZorgMail Account-ID recorded (`getZorgMailConfig`). There is deliberately **no per-user enablement** — unlike Zivver, a ZorgMail afzender is a name in the message payload with nothing to verify against, so any behandelaar of a configured practice can send (ADR-041 §3). 
         * @summary Send a generated letter to the huisarts as a MEDVRI over ZorgMail
         * @param {string} clientId Client UUID
         * @param {string} letterId Generated letter UUID
         * @param {SendLetterViaZorgMailRequest} sendLetterViaZorgMailRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async sendLetterViaZorgMail(clientId: string, letterId: string, sendLetterViaZorgMailRequest: SendLetterViaZorgMailRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SendLetterViaZorgMailResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.sendLetterViaZorgMail(clientId, letterId, sendLetterViaZorgMailRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['LettersApi.sendLetterViaZorgMail']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * LettersApi - factory interface
 */
export const LettersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = LettersApiFp(configuration)
    return {
        /**
         * Deletes a generated letter and its associated document from the client\'s dossier. This is a soft delete - the document is marked as deleted but not permanently removed.  Requires `documents.delete` permission. 
         * @summary Delete a generated letter
         * @param {string} clientId Client UUID
         * @param {string} letterId Generated letter UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteClientLetter(clientId: string, letterId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteClientLetter(clientId, letterId, options).then((request) => request(axios, basePath));
        },
        /**
         * Generate a letter PDF from a template for a specific client.  The PDF is saved as a document in the client\'s dossier and a GeneratedLetter record is created for audit purposes. The response includes the letter and document IDs for future reference.  Requires `letters.generate` permission. 
         * @summary Generate a letter PDF
         * @param {GenerateLetterRequest} generateLetterRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        generateLetter(generateLetterRequest: GenerateLetterRequest, options?: RawAxiosRequestConfig): AxiosPromise<GenerateLetterResponse> {
            return localVarFp.generateLetter(generateLetterRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the HTML a generated letter\'s PDF was rendered from, including any edits made before generating, so it can be reopened in the letter editor and generated again as a corrected letter. The HTML is returned as stored; it was sanitized when the letter was generated. A letter generated before its content was stored has none to return — see `editable` on `GeneratedLetterSummary` — and answers 404.  Requires `letters.generate` permission and access to the client. 
         * @summary Get a generated letter\'s stored content
         * @param {string} clientId Client UUID
         * @param {string} letterId Generated letter UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientLetter(clientId: string, letterId: string, options?: RawAxiosRequestConfig): AxiosPromise<GeneratedLetterContent> {
            return localVarFp.getClientLetter(clientId, letterId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns one page of the generated letters for a specific client, newest first. Each letter includes the template name, generation date, and the user who generated it.  Requires `letters.generate` permission. 
         * @summary List generated letters for a client
         * @param {string} clientId Client UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of letters per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientLetters(clientId: string, page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<GeneratedLetterListResponse> {
            return localVarFp.listClientLetters(clientId, page, size, options).then((request) => request(axios, basePath));
        },
        /**
         * Generates a preview of a letter with placeholders resolved. Returns rendered HTML (not PDF) and a list of any missing or empty placeholders.  This endpoint is used to show users what the letter will look like before generating the final PDF, and to warn them about any missing data.  Requires `letters.generate` permission. 
         * @summary Preview letter with resolved placeholders
         * @param {LetterPreviewRequest} letterPreviewRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewLetter(letterPreviewRequest: LetterPreviewRequest, options?: RawAxiosRequestConfig): AxiosPromise<LetterPreviewResponse> {
            return localVarFp.previewLetter(letterPreviewRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Sends a previously generated letter\'s PDF to an external recipient through the organization\'s Zivver secure-email gateway (ADR-028).  The caller must have enabled Zivver on their profile (giving them a validated sender address) and the organization must have Zivver SMTP configured. The per-message recipient verification policy is derived from `verificationMethod` and the optional `verificationDetails`.  Requires a FULL license and the `letters.generate` permission. 
         * @summary Send a generated letter to a recipient via Zivver secure email
         * @param {string} clientId Client UUID
         * @param {string} letterId Generated letter UUID
         * @param {SendLetterViaZivverRequest} sendLetterViaZivverRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendLetterViaZivver(clientId: string, letterId: string, sendLetterViaZivverRequest: SendLetterViaZivverRequest, options?: RawAxiosRequestConfig): AxiosPromise<SendLetterViaZivverResponse> {
            return localVarFp.sendLetterViaZivver(clientId, letterId, sendLetterViaZivverRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Renders a generated letter as a ZorgMail **MEDVRI** and hands it to Enovation\'s `edi-smtp` gateway, so it lands in the huisarts\'s own EPD filed against the right patient (#4121, ADR-041). This is the act the whole ZorgMail epic exists for.  **The recipient is stated, never derived.** `recipient` is what the sender confirmed — normally a candidate from `getZorgMailRecipient`, possibly one they corrected. The API will not resolve a huisarts on its own: the chain from zorgtraject to verwijzer to AGB code to mailbox can be wrong in ways only a person notices, and a letter sent to last year\'s verwijzer is a disclosure to the wrong care provider.  **A 200 means accepted, not delivered.** ZorgMail acknowledges the submission and validates the content afterwards; a content failure bounces asynchronously into the *practice\'s* own ZorgMail mailbox, which Scrivio does not read until #4122. The response is named accordingly, and the UI must be too (ADR-041 §7).  **Every outcome is durable.** Accepted or refused, the attempt is recorded and shown on the letter afterwards (`zorgMailSends` on `GeneratedLetterSummary`); a refusal carries the row\'s `sendId` on the problem detail. A letter to a huisarts that did not arrive is a clinical problem, so the outcome may not be a toast that disappears on the next navigation.  Gates, all server-side because frontend hiding is best-effort: a FULL license, the `letters.generate` permission, ZorgMail wired in this deployment, and the practice\'s ZorgMail Account-ID recorded (`getZorgMailConfig`). There is deliberately **no per-user enablement** — unlike Zivver, a ZorgMail afzender is a name in the message payload with nothing to verify against, so any behandelaar of a configured practice can send (ADR-041 §3). 
         * @summary Send a generated letter to the huisarts as a MEDVRI over ZorgMail
         * @param {string} clientId Client UUID
         * @param {string} letterId Generated letter UUID
         * @param {SendLetterViaZorgMailRequest} sendLetterViaZorgMailRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendLetterViaZorgMail(clientId: string, letterId: string, sendLetterViaZorgMailRequest: SendLetterViaZorgMailRequest, options?: RawAxiosRequestConfig): AxiosPromise<SendLetterViaZorgMailResponse> {
            return localVarFp.sendLetterViaZorgMail(clientId, letterId, sendLetterViaZorgMailRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * LettersApi - object-oriented interface
 */
export class LettersApi extends BaseAPI {
    /**
     * Deletes a generated letter and its associated document from the client\'s dossier. This is a soft delete - the document is marked as deleted but not permanently removed.  Requires `documents.delete` permission. 
     * @summary Delete a generated letter
     * @param {string} clientId Client UUID
     * @param {string} letterId Generated letter UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteClientLetter(clientId: string, letterId: string, options?: RawAxiosRequestConfig) {
        return LettersApiFp(this.configuration).deleteClientLetter(clientId, letterId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Generate a letter PDF from a template for a specific client.  The PDF is saved as a document in the client\'s dossier and a GeneratedLetter record is created for audit purposes. The response includes the letter and document IDs for future reference.  Requires `letters.generate` permission. 
     * @summary Generate a letter PDF
     * @param {GenerateLetterRequest} generateLetterRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public generateLetter(generateLetterRequest: GenerateLetterRequest, options?: RawAxiosRequestConfig) {
        return LettersApiFp(this.configuration).generateLetter(generateLetterRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the HTML a generated letter\'s PDF was rendered from, including any edits made before generating, so it can be reopened in the letter editor and generated again as a corrected letter. The HTML is returned as stored; it was sanitized when the letter was generated. A letter generated before its content was stored has none to return — see `editable` on `GeneratedLetterSummary` — and answers 404.  Requires `letters.generate` permission and access to the client. 
     * @summary Get a generated letter\'s stored content
     * @param {string} clientId Client UUID
     * @param {string} letterId Generated letter UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getClientLetter(clientId: string, letterId: string, options?: RawAxiosRequestConfig) {
        return LettersApiFp(this.configuration).getClientLetter(clientId, letterId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns one page of the generated letters for a specific client, newest first. Each letter includes the template name, generation date, and the user who generated it.  Requires `letters.generate` permission. 
     * @summary List generated letters for a client
     * @param {string} clientId Client UUID
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of letters per page
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listClientLetters(clientId: string, page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return LettersApiFp(this.configuration).listClientLetters(clientId, page, size, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Generates a preview of a letter with placeholders resolved. Returns rendered HTML (not PDF) and a list of any missing or empty placeholders.  This endpoint is used to show users what the letter will look like before generating the final PDF, and to warn them about any missing data.  Requires `letters.generate` permission. 
     * @summary Preview letter with resolved placeholders
     * @param {LetterPreviewRequest} letterPreviewRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public previewLetter(letterPreviewRequest: LetterPreviewRequest, options?: RawAxiosRequestConfig) {
        return LettersApiFp(this.configuration).previewLetter(letterPreviewRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Sends a previously generated letter\'s PDF to an external recipient through the organization\'s Zivver secure-email gateway (ADR-028).  The caller must have enabled Zivver on their profile (giving them a validated sender address) and the organization must have Zivver SMTP configured. The per-message recipient verification policy is derived from `verificationMethod` and the optional `verificationDetails`.  Requires a FULL license and the `letters.generate` permission. 
     * @summary Send a generated letter to a recipient via Zivver secure email
     * @param {string} clientId Client UUID
     * @param {string} letterId Generated letter UUID
     * @param {SendLetterViaZivverRequest} sendLetterViaZivverRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public sendLetterViaZivver(clientId: string, letterId: string, sendLetterViaZivverRequest: SendLetterViaZivverRequest, options?: RawAxiosRequestConfig) {
        return LettersApiFp(this.configuration).sendLetterViaZivver(clientId, letterId, sendLetterViaZivverRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Renders a generated letter as a ZorgMail **MEDVRI** and hands it to Enovation\'s `edi-smtp` gateway, so it lands in the huisarts\'s own EPD filed against the right patient (#4121, ADR-041). This is the act the whole ZorgMail epic exists for.  **The recipient is stated, never derived.** `recipient` is what the sender confirmed — normally a candidate from `getZorgMailRecipient`, possibly one they corrected. The API will not resolve a huisarts on its own: the chain from zorgtraject to verwijzer to AGB code to mailbox can be wrong in ways only a person notices, and a letter sent to last year\'s verwijzer is a disclosure to the wrong care provider.  **A 200 means accepted, not delivered.** ZorgMail acknowledges the submission and validates the content afterwards; a content failure bounces asynchronously into the *practice\'s* own ZorgMail mailbox, which Scrivio does not read until #4122. The response is named accordingly, and the UI must be too (ADR-041 §7).  **Every outcome is durable.** Accepted or refused, the attempt is recorded and shown on the letter afterwards (`zorgMailSends` on `GeneratedLetterSummary`); a refusal carries the row\'s `sendId` on the problem detail. A letter to a huisarts that did not arrive is a clinical problem, so the outcome may not be a toast that disappears on the next navigation.  Gates, all server-side because frontend hiding is best-effort: a FULL license, the `letters.generate` permission, ZorgMail wired in this deployment, and the practice\'s ZorgMail Account-ID recorded (`getZorgMailConfig`). There is deliberately **no per-user enablement** — unlike Zivver, a ZorgMail afzender is a name in the message payload with nothing to verify against, so any behandelaar of a configured practice can send (ADR-041 §3). 
     * @summary Send a generated letter to the huisarts as a MEDVRI over ZorgMail
     * @param {string} clientId Client UUID
     * @param {string} letterId Generated letter UUID
     * @param {SendLetterViaZorgMailRequest} sendLetterViaZorgMailRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public sendLetterViaZorgMail(clientId: string, letterId: string, sendLetterViaZorgMailRequest: SendLetterViaZorgMailRequest, options?: RawAxiosRequestConfig) {
        return LettersApiFp(this.configuration).sendLetterViaZorgMail(clientId, letterId, sendLetterViaZorgMailRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ZorgmailGatewayTestsApi - axios parameter creator
 */
export const ZorgmailGatewayTestsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Sends a fixed, synthetic MEDVRI — a fictitious patient, no letter — to `gateway.validatie@lms.lifeline.nl` or `gateway.test@lms.lifeline.nl` (#4710). Enovation approve Scrivio\'s connection against these messages before they let its ASP account send on the practice\'s behalf.  **The caller picks the gateway and nothing else.** The address is never accepted from the request; it comes off the `gateway` value.  The message goes through the same assembly, DTD validation and gateway as a real letter, and carries `testindicator=Y` whenever the deployment\'s test indicator is on, so a test that is accepted proves the path a letter takes.  **Accepted is not valid.** The response is the gateway\'s synchronous answer as it is. The validatieverslag arrives later in the practice\'s own ZorgMail mailbox, which Scrivio does not read (#4122). Every test is recorded, accepted or refused.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Send a test MEDVRI to one of Enovation\'s ZorgMail test gateways
         * @param {string} organizationId Organization UUID
         * @param {SendZorgMailGatewayTestRequest} sendZorgMailGatewayTestRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendZorgMailGatewayTest: async (organizationId: string, sendZorgMailGatewayTestRequest: SendZorgMailGatewayTestRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('sendZorgMailGatewayTest', 'organizationId', organizationId)
            // verify required parameter 'sendZorgMailGatewayTestRequest' is not null or undefined
            assertParamExists('sendZorgMailGatewayTest', 'sendZorgMailGatewayTestRequest', sendZorgMailGatewayTestRequest)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/zorgmail/gateway-tests`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(sendZorgMailGatewayTestRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ZorgmailGatewayTestsApi - functional programming interface
 */
export const ZorgmailGatewayTestsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ZorgmailGatewayTestsApiAxiosParamCreator(configuration)
    return {
        /**
         * Sends a fixed, synthetic MEDVRI — a fictitious patient, no letter — to `gateway.validatie@lms.lifeline.nl` or `gateway.test@lms.lifeline.nl` (#4710). Enovation approve Scrivio\'s connection against these messages before they let its ASP account send on the practice\'s behalf.  **The caller picks the gateway and nothing else.** The address is never accepted from the request; it comes off the `gateway` value.  The message goes through the same assembly, DTD validation and gateway as a real letter, and carries `testindicator=Y` whenever the deployment\'s test indicator is on, so a test that is accepted proves the path a letter takes.  **Accepted is not valid.** The response is the gateway\'s synchronous answer as it is. The validatieverslag arrives later in the practice\'s own ZorgMail mailbox, which Scrivio does not read (#4122). Every test is recorded, accepted or refused.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Send a test MEDVRI to one of Enovation\'s ZorgMail test gateways
         * @param {string} organizationId Organization UUID
         * @param {SendZorgMailGatewayTestRequest} sendZorgMailGatewayTestRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async sendZorgMailGatewayTest(organizationId: string, sendZorgMailGatewayTestRequest: SendZorgMailGatewayTestRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgMailGatewayTestResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.sendZorgMailGatewayTest(organizationId, sendZorgMailGatewayTestRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgmailGatewayTestsApi.sendZorgMailGatewayTest']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ZorgmailGatewayTestsApi - factory interface
 */
export const ZorgmailGatewayTestsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ZorgmailGatewayTestsApiFp(configuration)
    return {
        /**
         * Sends a fixed, synthetic MEDVRI — a fictitious patient, no letter — to `gateway.validatie@lms.lifeline.nl` or `gateway.test@lms.lifeline.nl` (#4710). Enovation approve Scrivio\'s connection against these messages before they let its ASP account send on the practice\'s behalf.  **The caller picks the gateway and nothing else.** The address is never accepted from the request; it comes off the `gateway` value.  The message goes through the same assembly, DTD validation and gateway as a real letter, and carries `testindicator=Y` whenever the deployment\'s test indicator is on, so a test that is accepted proves the path a letter takes.  **Accepted is not valid.** The response is the gateway\'s synchronous answer as it is. The validatieverslag arrives later in the practice\'s own ZorgMail mailbox, which Scrivio does not read (#4122). Every test is recorded, accepted or refused.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Send a test MEDVRI to one of Enovation\'s ZorgMail test gateways
         * @param {string} organizationId Organization UUID
         * @param {SendZorgMailGatewayTestRequest} sendZorgMailGatewayTestRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        sendZorgMailGatewayTest(organizationId: string, sendZorgMailGatewayTestRequest: SendZorgMailGatewayTestRequest, options?: RawAxiosRequestConfig): AxiosPromise<ZorgMailGatewayTestResponse> {
            return localVarFp.sendZorgMailGatewayTest(organizationId, sendZorgMailGatewayTestRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ZorgmailGatewayTestsApi - object-oriented interface
 */
export class ZorgmailGatewayTestsApi extends BaseAPI {
    /**
     * Sends a fixed, synthetic MEDVRI — a fictitious patient, no letter — to `gateway.validatie@lms.lifeline.nl` or `gateway.test@lms.lifeline.nl` (#4710). Enovation approve Scrivio\'s connection against these messages before they let its ASP account send on the practice\'s behalf.  **The caller picks the gateway and nothing else.** The address is never accepted from the request; it comes off the `gateway` value.  The message goes through the same assembly, DTD validation and gateway as a real letter, and carries `testindicator=Y` whenever the deployment\'s test indicator is on, so a test that is accepted proves the path a letter takes.  **Accepted is not valid.** The response is the gateway\'s synchronous answer as it is. The validatieverslag arrives later in the practice\'s own ZorgMail mailbox, which Scrivio does not read (#4122). Every test is recorded, accepted or refused.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary Send a test MEDVRI to one of Enovation\'s ZorgMail test gateways
     * @param {string} organizationId Organization UUID
     * @param {SendZorgMailGatewayTestRequest} sendZorgMailGatewayTestRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public sendZorgMailGatewayTest(organizationId: string, sendZorgMailGatewayTestRequest: SendZorgMailGatewayTestRequest, options?: RawAxiosRequestConfig) {
        return ZorgmailGatewayTestsApiFp(this.configuration).sendZorgMailGatewayTest(organizationId, sendZorgMailGatewayTestRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



